import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Table,
  Space,
  Tag,
  notification,
  Button,
  Popconfirm,
} from "antd";
import { casesService } from "../services/casesService";
import { doctorsService } from "../services/doctorsService";
import { CasesDTO } from "../models/Cases";
import { DoctorsDTO } from "../models/Doctors";
import styles from "./CaseReport.module.css";
import { useAuth } from "../contexts/authContext";
import FilterPanel, { FilterConfig } from "../components/common/FilterPanel";
import { isAslDoctor } from "../config/utils";
import { FolderViewOutlined, MedicineBoxOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { routes } from "../routes";
import { useLoaderProvider } from "../contexts/LoaderContext";
import DoctorModal from "../components/common/DoctorModal";
import { eventBus } from "../eventBus";

const { Title } = Typography;

const CaseReport: React.FC = () => {
  const { showLoader, hideLoader } = useLoaderProvider();
  const [cases, setCases] = useState<CasesDTO[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [refreshCases, setRefreshCases] = useState<boolean>(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorsDTO | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const navigate = useNavigate();
  const { loggedDoctor, loggedUser } = useAuth();
  const isAsl = isAslDoctor(loggedUser!);

  const pageSize = 25; // Define pageSize as a constant

  const handleViewCase = (caseId: number) => {
    navigate(routes.CASE_DETAILS.replace(":id", caseId.toString()));
  };

  const takingCase = async (caseId: number) => {
    try {
      showLoader("Presa in carico del caso...");
      await casesService.changeStatus(caseId, "IN_PROGRESS");
      setRefreshCases(!refreshCases);
      eventBus.emit('noteRead');
    } catch (error) {
      console.error("Errore durante la conferma del caso:", error);
    } finally {
      hideLoader();
    }
  };

  const caseStatus = isAsl
    ? [
        { value: "DRAFT", label: "Bozza" },
        { value: "OPEN", label: "Aperto" },
        { value: "IN_PROGRESS", label: "In Corso" },
        { value: "CLOSED", label: "Chiuso" },
      ]
    : [
        { value: "OPEN", label: "Aperto" },
        { value: "IN_PROGRESS", label: "In Corso" },
        { value: "CLOSED", label: "Chiuso" },
      ];

  const filterConfig: FilterConfig[] = [
    {
      name: "caseStatus.equals",
      label: "Stato",
      type: "select",
      options: caseStatus,
    },
    {
      name: "patient.lastName.contains",
      label: "Cognome Paziente",
      type: "input",
    },
    {
      name: "openingDate",
      label: "Data Apertura",
      type: "dateRange",
    },
  ];

  const showDoctorModal = async (doctorId: number) => {
    try {
      showLoader("Caricamento dei dati del dottore...");
      const doctor = await doctorsService.getDoctor(doctorId);
      setSelectedDoctor(doctor);
      setIsModalVisible(true);
    } catch (error) {
      notification.error({
        message: "Errore nel caricamento dei dati del dottore",
      });
      console.error("Errore nel caricamento dei dati del dottore:", error);
    } finally {
      hideLoader();
    }
  };

  const columns = [
    {
      title: "ID Caso",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Stato",
      dataIndex: "caseStatus",
      key: "caseStatus",
      render: (caseStatus: "DRAFT" | "OPEN" | "IN_PROGRESS" | "CLOSED") => {
        switch (caseStatus) {
          case "DRAFT":
            return <Tag color="blue">BOZZA</Tag>;
          case "OPEN":
            return <Tag color="green">APERTO</Tag>;
          case "IN_PROGRESS":
            return <Tag color="orange">PRESO IN CARICO</Tag>;
          case "CLOSED":
            return <Tag color="red">CHIUSO</Tag>;
          default:
            return "";
        }
      },
    },
    {
      title: "Data Creazione",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Nome Paziente",
      key: "patientName",
      render: (text: any, record: CasesDTO) =>
        `${record.patient?.firstName} ${record.patient?.lastName}`,
    },
    {
      title: "ID Anonimo Paziente",
      dataIndex: ["patient", "anonymizedId"],
      key: "patientAnonymizedId",
    },
    {
      title: "Dottore ASL",
      key: "aslDoctor",
      render: (text: any, record: CasesDTO) => {
        return (
          <Button
            color="default"
            type="link"
            onClick={() => showDoctorModal(record.createdBy?.id!)}
          >
            {`${record.createdBy?.user?.firstName} ${record.createdBy?.user?.lastName}`}
          </Button>
        );
      },
    },
    {
      title: "Dottore Spec.",
      key: "specialistDoctor",
      render: (text: any, record: CasesDTO) => {
        if (!record.takeInChargeBy) return "N/A";
        return (
          <Button
            color="default"
            type="link"
            onClick={() => showDoctorModal(record.takeInChargeBy?.id!)}
          >
            {`${record.takeInChargeBy?.user?.firstName} ${record.takeInChargeBy?.user?.lastName}`}
          </Button>
        );
      },
    },
    {
      title: "Data Conferma",
      dataIndex: "openingDate",
      key: "openingDate",
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString() : "N/A",
    },
    {
      title: "Data Chiusura",
      dataIndex: "closingDate",
      key: "closingDate",
      render: (date: string) =>
        date ? new Date(date).toLocaleDateString() : "N/A",
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_: any, record: CasesDTO) => (
        <Space direction="vertical" size="small">
          <Button
            type="default"
            icon={<FolderViewOutlined />}
            onClick={() => handleViewCase(record.id!)}
          >
            Visualizza
          </Button>
          {!isAsl && !record.takeInChargeBy && (
            <Popconfirm
              title="Prendi in carico"
              description="Sei sicuro di voler prendere in carico il caso?"
              onConfirm={() => takingCase(record.id!)}
              okText="Si"
              cancelText="No"
            >
              <Button type="primary" icon={<MedicineBoxOutlined />}>
                Prendi in carico
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const fetchCases = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        const params = {
          page: page - 1, // API typically uses 0-based indexing
          size: pageSize,
          sort: ["createdAt,desc"],
          ...(isAsl
            ? { "createdById.equals": loggedDoctor!.id }
            : { "caseStatus.notEquals": "DRAFT" }),
          ...filters,
        } as any;

        const [items, count] = await Promise.all([
          casesService.getAllCases(params),
          casesService.countCases(params),
        ]);

        setCases(items);
        setTotalItems(count);
      } catch (error) {
        notification.error({ message: "Errore nel caricamento dei casi" });
        console.error("Errore nel caricamento dei casi:", error);
      } finally {
        setLoading(false);
      }
    },
    [loggedDoctor, filters, isAsl]
  );

  useEffect(() => {
    if (loggedDoctor) fetchCases(currentPage);
  }, [loggedDoctor, filters, refreshCases, isAsl, currentPage, fetchCases]);

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
  };

  const handleFilterChange = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  return (
    <div className={styles.reportContainer}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Title level={2}>Report Casi</Title>
        <FilterPanel
          filters={filterConfig}
          onFilterChange={handleFilterChange}
          showExport={true}
          exportService={casesService.exportCases}
          exportFileName="cases_export.csv"
        />
        <Table
          columns={columns}
          dataSource={cases}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      </Space>
      <DoctorModal
        doctor={selectedDoctor!}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        visible={isModalVisible}
      />
    </div>
  );
};

export default CaseReport;
